import { useEffect, useRef, useState } from 'react';
import { TextField, CircularProgress, InputAdornment } from '@mui/material';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

export interface AddressParts {
    fullAddress: string;   // e.g. "6624 NW 107th Pl"
    city:        string;   // e.g. "Doral"
    state:       string;   // e.g. "FL"
    zipCode:     string;   // e.g. "33178"
}

interface Props {
    value: string;
    onChange: (raw: string) => void;
    onPlaceSelected: (parts: AddressParts) => void;
    disabled?: boolean;
    label?: string;
}

let placesPromise: Promise<void> | null = null;

function loadPlacesLibrary(): Promise<void> {
    if (!placesPromise) {
        setOptions({ key: import.meta.env.VITE_GOOGLE_MAPS_KEY as string });
        placesPromise = importLibrary('places').then(() => undefined);
    }
    return placesPromise;
}

function getComponent(
    components: google.maps.GeocoderAddressComponent[],
    type: string,
    nameType: 'long_name' | 'short_name' = 'long_name',
): string {
    return components.find(c => c.types.includes(type))?.[nameType] ?? '';
}

export const AddressAutocomplete = ({
    value,
    onChange,
    onPlaceSelected,
    disabled,
    label = 'Job Site Address',
}: Props) => {
    const inputRef  = useRef<HTMLInputElement>(null);
    const acRef     = useRef<google.maps.places.Autocomplete | null>(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (disabled) return;
        loadPlacesLibrary()
            .then(() => setReady(true))
            .catch(err => console.error('Google Places failed to load:', err));
    }, [disabled]);

    useEffect(() => {
        if (disabled || !ready || !inputRef.current || acRef.current) return;

        acRef.current = new google.maps.places.Autocomplete(inputRef.current, {
            componentRestrictions: { country: 'us' },
            fields: ['address_components', 'formatted_address'],
            types: ['address'],
        });

        acRef.current.addListener('place_changed', () => {
            const place = acRef.current!.getPlace();
            if (!place.address_components) return;

            const comps   = place.address_components;
            const streetNumber = getComponent(comps, 'street_number');
            // short_name matches abbreviated streets (NW, Ave) common in legacy CSV.
            const route = getComponent(comps, 'route', 'short_name')
                || getComponent(comps, 'route');
            const street  = `${streetNumber} ${route}`.trim();
            const city    = getComponent(comps, 'locality') || getComponent(comps, 'sublocality_level_1');
            const state   = getComponent(comps, 'administrative_area_level_1', 'short_name');
            const zipCode = getComponent(comps, 'postal_code');

            onChange(street);
            onPlaceSelected({ fullAddress: street, city, state, zipCode });
        });
    }, [ready, onChange, onPlaceSelected, disabled]);

    return (
        <TextField
            label={label}
            size="small"
            fullWidth
            value={value}
            onChange={e => onChange(e.target.value)}
            inputRef={inputRef}
            autoComplete="off"
            disabled={disabled}
            InputProps={!ready && !disabled ? {
                endAdornment: (
                    <InputAdornment position="end">
                        <CircularProgress size={14} />
                    </InputAdornment>
                ),
            } : undefined}
        />
    );
};
